/**
 * Several styles in one upload, all estimated (JF 2026-10-13: "一次传多款，逐款自动估价"): each style is read and priced
 * on its own — its files plus the files that belong to no style (price lists, mails) — "don't ask, assume", saved as its
 * own cost sheet, and at the end the styles are put into one style group. One style failing does not stop the others.
 * Since 2026-10-16 the SERVER does it (estimate jobs): the page converts each style's files and hands them over; after
 * that the page may be closed — the styles go on, and "my estimates" (top bar) shows them. "Stop" stops them.
 */
import { api, type Job, type JobStatus, type Part } from "../../shared/api.ts";
import { fileToParts, type PageSel } from "./files.ts";
import { flagStale, STALE_MSG } from "../../shared/stale.ts";

export type BatchStatus = "wait" | "read" | "price" | "save" | "done" | "err";
export interface BatchStyle {
  key: string; style_no: string; style_name: string; files: File[];
  /** per file: only these pages (a file holding several styles) */ pages?: (PageSel | undefined)[];
  status: BatchStatus; msg?: string; sheetId?: string; customer?: string; jobId?: string;
  /** when this style started / finished (ms), for the overall progress */ t0?: number; t1?: number;
  /** the quote per order quantity (tiered quote: every quantity; else the sheet's own) — USD; fx = the sheet's rate */
  quotes?: { qty: number; qfob: number }[]; fx?: number;
}

let handing = false;
let current: string | null = null;
let stopAsked = false;
/** the files are still being handed to the server (closing the browser now would lose the styles not sent yet) */
export const busyBatch = () => handing;
/** stop the batch: the styles not sent yet are not sent, the server stops the others */
export async function stopBatch(): Promise<void> {
  stopAsked = true;
  if (current) await api.jobCancelBatch(current).catch(() => undefined);
}

const MAX_MB = 30;
const ENDED: JobStatus[] = ["done", "err", "cancelled"];
const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));
type T = (en: string, zh: string) => string;

/** how far the AI call of a job has got (the same words as the single estimate) */
export function progressText(kind: Job["pkind"], x: { elapsedMs?: number; typicalMs?: number; lines?: number; tierQty?: number; items?: number }, t: T): string {
  const sec = Math.round((x.elapsedMs ?? 0) / 1000);
  const what = kind === "extract"
    ? (x.items ? t(`writing checklist item ${x.items}`, `AI 正在写第 ${x.items} 项清单`) : t("reading the files", "AI 正在读文件"))
    : x.tierQty ? t(`writing the tiered quote, quantity ${x.tierQty}`, `AI 正在写阶梯报价第 ${x.tierQty} 档`)
    : x.lines ? t(`writing line ${x.lines}`, `AI 正在写第 ${x.lines} 行`) : t("pricing against our history", "AI 正在对照历史定价");
  const usual = x.typicalMs ? t(` · usually about ${Math.round(x.typicalMs / 1000)} s`, ` · 通常约 ${Math.round(x.typicalMs / 1000)} 秒`) : "";
  return `${what} · ${t(`${sec} s`, `已用 ${sec} 秒`)}${usual}`;
}

/** a server job as one row of the batch list */
export function jobRow(j: Job, t: T): Partial<BatchStyle> {
  const st: BatchStatus = j.status === "queued" ? "wait" : j.status === "extract" ? "read" : j.status === "price" ? "price" : j.status === "save" ? "save"
    : j.status === "done" ? "done" : j.status === "answer" ? "wait" : "err";
  const msg = j.status === "queued" ? t("on the server — queued (the page may be closed)", "已交给服务器 · 排队中（可以关页面）")
    : j.status === "cancelled" ? t("stopped", "已停止") : j.status === "err" ? (j.error || t("failed", "失败")) : j.status === "save" ? t("saving", "保存") : "";
  return {
    status: st, msg, jobId: j.id, sheetId: j.sheetId || undefined, customer: j.customer, quotes: j.quotes || undefined, fx: j.fx ?? undefined,
    t0: j.startedAt ? Date.parse(j.startedAt) : undefined, t1: j.finishedAt ? Date.parse(j.finishedAt) : undefined,
  };
}

export async function runBatch(list: BatchStyle[], opts: {
  notes: string; lang: "zh" | "en";
  /** tiered quote: null = off; [] = the quantities each style's files ask for */
  tiers: number[] | null;
  onUpdate(list: BatchStyle[]): void;
  t: T;
}): Promise<{ groupId: string | null; done: number }> {
  const { t, lang } = opts;
  const out = list.map((x) => ({ ...x }));
  const upd = (i: number, p: Partial<BatchStyle>) => { out[i] = { ...out[i], ...p }; opts.onUpdate([...out]); };
  handing = true; stopAsked = false;
  let sent = 0, batchId = "";
  // 1. hand each style to the server (the page converts its files first — PDF pages etc.)
  try {
    batchId = (await api.jobBatch(out.length)).id;
    current = batchId;
    for (const [i, st] of out.entries()) {
      if (stopAsked) { upd(i, { status: "err", msg: t("stopped", "已停止") }); continue; }
      try {
        upd(i, { status: "read", msg: t("handing the files to the server", "正在把文件交给服务器") });
        const parts: Part[] = [];
        for (const [k, f] of st.files.entries()) parts.push(...(await fileToParts(f, k + 1, undefined, st.pages?.[k])));
        // shared files (price lists, mails) may mention other styles: say which one this is
        parts.push({ type: "text", text: `[STYLE TO COST] Cost ONLY this style: ${[st.style_no, st.style_name].filter(Boolean).join(" · ")}. Ignore other styles that appear in shared files.` });
        if (opts.notes.trim()) parts.push({ type: "text", text: "[AM NOTES]\n" + opts.notes.trim() });
        const mb = new Blob([JSON.stringify(parts)]).size / 1024 / 1024;
        if (mb > MAX_MB) throw new Error(t(`files too large for the AI (${mb.toFixed(1)} MB)`, `文件太大，AI 读不了（${mb.toFixed(1)} MB）`));
        const j = await api.jobCreate({ kind: "batch", batchId, title: [st.style_no, st.style_name].filter(Boolean).join(" · "), parts, ask: false, lang, tiers: opts.tiers,
          notes: opts.notes, files: st.files.map((f) => ({ name: f.name, size: f.size, type: f.type })) });
        sent++;
        upd(i, jobRow(j, t));
      } catch (e) {
        // an outdated page (a new version was deployed): every style would fail the same way — stop and ask to refresh
        if (flagStale(e)) { for (let k = i; k < out.length; k++) upd(k, { status: "err", msg: STALE_MSG }); break; }
        upd(i, { status: "err", msg: e instanceof Error ? e.message : String(e) });
      }
    }
  } finally {
    handing = false;
    // the styles that never reached the server do not count: the style group is made from the ones that did
    if (batchId && sent < out.length) await api.jobBatchSize(batchId, sent).catch(() => undefined);
  }
  if (!sent) { current = null; return { groupId: null, done: 0 }; }
  // 2. follow the server (only for this page — closing it changes nothing)
  let groupId: string | null = null, endedAt = 0;
  for (;;) {
    const L = await api.jobs(batchId).catch(() => null);
    if (L) {
      for (const j of L.items) {
        const i = out.findIndex((x) => x.jobId === j.id);
        if (i < 0) continue;
        const row = jobRow(j, t);
        if (j.pid && (j.status === "extract" || j.status === "price")) {
          const x = await api.aiProgress(j.pid, j.pkind).catch(() => null);
          if (x?.running) row.msg = progressText(j.pkind, x, t);
        }
        upd(i, row);
      }
      groupId = L.groupId || null;
      if (L.items.length >= sent && L.items.every((j) => ENDED.includes(j.status))) {
        endedAt ||= Date.now();
        // the server makes the style group right after the last style; give it a moment
        if (groupId || !L.items.some((j) => j.status === "done") || Date.now() - endedAt > 8000) break;
      }
    }
    await sleep(1500);
  }
  current = null;
  return { groupId, done: out.filter((x) => x.status === "done").length };
}

/** a rough time for the confirmation: about 2 minutes a style (reading + pricing), more with a tiered quote */
export const batchMinutes = (styles: number, tiers: boolean) => Math.max(1, Math.round(styles * (tiers ? 3.5 : 2)));
